import type { ApiNotification, ApiNotificationType } from '@/shared/api';

/** What the domain emits; `review` and `system` were never real. */
export type NotificationType = ApiNotificationType;

/** How long ago, as a translation key plus its values. */
export type RelativeTime = {
  key: 'minutesAgo' | 'hoursAgo' | 'yesterdayAt' | 'daysAgo';
  params: Record<string, string | number>;
};

export type AppNotification = {
  id: number;
  type: NotificationType;
  /** Values for `notifications:texts.{type}`. */
  params: Record<string, string | number>;
  time: RelativeTime;
  read: boolean;
};

/**
 * The translation-key name for a type.
 *
 * The API value carries a dot (`booking.created`) and i18next reads a dot as a
 * path separator, so a key named after the value would be looked up as
 * `types → booking → created` and render as the raw key instead.
 */
const LOCALE_KEY: Record<NotificationType, string> = {
  'booking.created': 'bookingCreated',
  'booking.cancelled': 'bookingCancelled',
  'booking.rescheduled': 'bookingRescheduled',
  'billing.members_suspended': 'billingMembersSuspended',
  'review.removed': 'reviewRemoved',
  'review.report_resolved': 'reviewReportResolved',
  'portfolio.removed': 'portfolioRemoved',
};

export const localeKey = (type: NotificationType): string => LOCALE_KEY[type];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "10 min ago" is presentation, not data: the API sends an instant and the
 * phrasing belongs to whoever is reading it, in their language and timezone.
 */
export const relativeTime = (iso: string | null, now: Date = new Date()): RelativeTime => {
  const elapsed = iso === null ? 0 : Math.max(0, now.getTime() - new Date(iso).getTime());

  if (elapsed < HOUR) return { key: 'minutesAgo', params: { count: Math.floor(elapsed / MINUTE) } };
  if (elapsed < DAY) return { key: 'hoursAgo', params: { count: Math.floor(elapsed / HOUR) } };

  if (elapsed < 2 * DAY) {
    const at = new Date(iso ?? Date.now());
    const time = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;

    return { key: 'yesterdayAt', params: { time } };
  }

  return { key: 'daysAgo', params: { count: Math.floor(elapsed / DAY) } };
};

export const toNotification = (dto: ApiNotification, now?: Date): AppNotification => ({
  id: dto.id,
  type: dto.type,
  params: {
    client: dto.payload.clientName ?? '',
    service: dto.payload.serviceName ?? '',
    ...(dto.payload.count !== undefined && { count: dto.payload.count }),
    ...(dto.payload.reason !== undefined && { reason: dto.payload.reason }),
  },
  time: relativeTime(dto.createdAt, now),
  read: dto.readAt !== null,
});
